// Plain-text rendering of Liquid template text (SPT-2316), with no imports so both the template editor and
// the workflow builder can use it. Close to, not the same as, the API renderer: wherever the API preview is
// available it wins; this covers lists, cards and the moments before the preview answers.

const MULTIWORD_TOKEN = /^[A-Za-z][A-Za-z0-9]*(?:[ \t]+[A-Za-z0-9]+)+$/
const variableName = (raw) => {
	const name = String(raw || '').trim()
	return MULTIWORD_TOKEN.test(name) ? name.toLowerCase().split(/\s+/).join('_') : name
}
const humanize = (name) =>
	String(name || '')
		.split(/[_.\s]+/)
		.filter(Boolean)
		.map((word, index) => (index ? word : word.charAt(0).toUpperCase() + word.slice(1)))
		.join(' ')

const TAG = '{%-?\\s*'
const END = '\\s*-?%}'
const IF_BLOCK = new RegExp(`${TAG}(if|unless)\\s+([^%]*?)${END}((?:(?!${TAG}(?:if|unless)\\b)[\\s\\S])*?)${TAG}end(?:if|unless)${END}`)
const BRANCH_TAG = new RegExp(`${TAG}(elsif|else)\\b([^%]*?)${END}`, 'g')

const literal = (value) => {
	const text = String(value ?? '').trim()
	if (/^["'].*["']$/.test(text)) return text.slice(1, -1)
	if (text === 'nil' || text === 'null') return null
	if (text === 'true' || text === 'false') return text === 'true'
	return text !== '' && !Number.isNaN(Number(text)) ? Number(text) : undefined
}

// A single `var`, `var == x`, `var > n`... against the samples. undefined when it cannot be decided.
const evaluateCondition = (condition, samples) => {
	const match = /^\s*([A-Za-z_][\w.]*)\s*(==|!=|>=|<=|>|<)?\s*(.*?)\s*$/.exec(condition)
	if (!match || /\s(and|or)\s/.test(condition)) return undefined
	const [, name, operator, right] = match
	if (!(name in samples)) return undefined
	const left = samples[name]
	const leftValue = left !== '' && left !== null && !Number.isNaN(Number(left)) ? Number(left) : left
	if (!operator) return Boolean(leftValue) && leftValue !== ''
	const rightValue = ['blank', 'empty'].includes(right) ? '' : literal(right)
	if (rightValue === undefined) return undefined
	switch (operator) {
		case '==':
			return leftValue === rightValue || (rightValue === '' && !leftValue)
		case '!=':
			return !(leftValue === rightValue || (rightValue === '' && !leftValue))
		case '>':
			return leftValue > rightValue
		case '<':
			return leftValue < rightValue
		case '>=':
			return leftValue >= rightValue
		default:
			return leftValue <= rightValue
	}
}

// Picks the branch of one if/unless block the samples choose; the first branch when they cannot decide.
const resolveBranch = (keyword, condition, content, samples) => {
	const branches = []
	let last = 0
	let current = { condition, negate: keyword === 'unless' }
	content.replace(BRANCH_TAG, (tag, kind, next, offset) => {
		branches.push({ ...current, text: content.slice(last, offset) })
		current = kind === 'else' ? { condition: null } : { condition: next }
		last = offset + tag.length
		return tag
	})
	branches.push({ ...current, text: content.slice(last) })
	for (const branch of branches) {
		if (branch.condition === null) return branch.text
		const result = evaluateCondition(branch.condition, samples)
		if (result === undefined) return branches[0].text
		if (branch.negate ? !result : result) return branch.text
	}
	return ''
}

// Lightweight plain-text render of a template with the sample values: `{% if %}` blocks take the branch the
// samples choose (else the first), other tags are dropped (a loop body is kept once, comments go), and each
// {{variable}} becomes its sample, else its `| default: "…"`, else — `unknown: 'label'` — a readable name, or
// stays as typed. Close to, not the same as, the API renderer: the API's preview is used whenever it answers.
export const renderPlainText = (text, variables = [], { unknown = 'keep' } = {}) => {
	const samples = (variables || []).reduce((all, variable) => (variable?.token ? { ...all, [variable.token]: variable.sample } : all), {})
	let source = String(text || '').replace(new RegExp(`${TAG}comment${END}[\\s\\S]*?${TAG}endcomment${END}`, 'g'), '')
	for (let guard = 0; guard < 50 && IF_BLOCK.test(source); guard += 1)
		source = source.replace(IF_BLOCK, (_, keyword, condition, content) => resolveBranch(keyword, condition, content, samples))
	source = source.replace(new RegExp(`${TAG}[\\s\\S]*?${END}`, 'g'), '')
	return source.replace(/{{-?\s*([^}|]+?)\s*(\|[^}]*)?-?}}/g, (match, raw, filters) => {
		const name = variableName(raw)
		const sample = samples[name]
		if (sample !== undefined && sample !== null && String(sample) !== '') return String(sample)
		const fallback = /default:\s*["']([^"']*)["']/.exec(filters || '')
		if (fallback) return fallback[1]
		return unknown === 'label' ? humanize(name) : match
	})
}

// A template subject as a one-line label (step cards, pickers, history): `{% %}` blocks resolved (the first
// branch, with no sample data), other tags dropped, `{{Variable}}` tokens kept as typed.
export const subjectLine = (text) => renderPlainText(text).replace(/\s+/g, ' ').trim()

// A placeholder as a reader would name it: "First Name" → "First name", "jobs_available_count | delimiter" →
// "Jobs available count" (filters dropped).
export const placeholderLabel = (raw) => {
	const words = String(raw || '')
		.split('|')[0]
		.trim()
		.split(/[_.\s]+/)
		.filter(Boolean)
		.map((word) => word.toLowerCase())
	return words.length ? words[0].charAt(0).toUpperCase() + words.join(' ').slice(1) : ''
}

// Template text split into plain text and readable placeholders, for showing a template without raw Liquid when
// no rendered preview is at hand: `{% %}` blocks resolve as in subjectLine, `| default: "…"` gives its text, and every
// other {{ variable }} becomes { type: 'placeholder', label }.
export const humanizedParts = (text) => {
	const source = renderPlainText(text)
	const parts = []
	let last = 0
	source.replace(/{{-?\s*([\s\S]*?)\s*-?}}/g, (match, raw, offset) => {
		if (offset > last) parts.push({ type: 'text', value: source.slice(last, offset) })
		const label = placeholderLabel(raw)
		if (label) parts.push({ type: 'placeholder', label })
		last = offset + match.length
		return match
	})
	if (last < source.length) parts.push({ type: 'text', value: source.slice(last) })
	return parts
}

// The same, flattened to one line with placeholders in brackets: "Hi [First name], there are [Jobs available count]…".
export const humanizedLine = (text) =>
	humanizedParts(text)
		.map((part) => (part.type === 'text' ? part.value : `[${part.label}]`))
		.join('')
		.replace(/\s+/g, ' ')
		.trim()
